import { useQuery } from "@apollo/client";
import { Snackbar, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Alert from "../common/Alert";
import Error from "../common/Error";
import Loading from "../common/Loading";
import UserId from "../common/UserId";
import BusStop from "../images/busStop.svg";
import { secondsToTime } from "../utils/timeHelpers";
import Chart from "./Chart";
import ExcludedList from "./ExcludedList";
import FavouritesList from "./FavouritesList";
import LoadMoreButton from "./LoadMoreButton";
import { GetSessionsType, GetSessionsVariables, sessionsQuery } from "./Profile.gql";
import { Container, ImageContainer, StyledImage } from "./Profile.sc";
import SessionsList from "./SessionsList";
import Settings from "./Settings";
import LoadLessButton from "./ShowLessButton";

interface LocationState {
    deletedSession: boolean;
    deletedSetting: boolean;
}

interface SnackbarProps {
    open: boolean;
    text: string;
    variant: "success" | "error" | "warning";
}

const Profile: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState<SnackbarProps>({ open: false, text: "", variant: "success" });

    React.useEffect(() => {
        if ((location.state as LocationState)?.deletedSession) {
            setSnackbar({ open: true, text: t("success_deleting_session"), variant: "success" });
        }

        if ((location.state as LocationState)?.deletedSetting) {
            setSnackbar({ open: true, text: t("success_deleting_setting"), variant: "success" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbar({ open: false, text: "", variant: "success" });
    };

    const { data, loading, fetchMore } = useQuery<GetSessionsType, GetSessionsVariables>(sessionsQuery, {
        variables: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            userId: localStorage.getItem("userId")!,
            limit: 3,
            offset: 0,
        },
        fetchPolicy: "network-only",
    });

    const onLoadMore = () => {
        setIsLoadingMore(true);
        fetchMore({
            variables: {
                limit: 3,
                offset: data?.sessions.nodes.length ?? 0,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                setIsLoadingMore(false);
                const previousData = previousResult.sessions;
                const newData = fetchMoreResult?.sessions ?? { nodes: [], totalCount: 0, totalDuration: 0 };
                return {
                    sessions: {
                        ...previousData,
                        nodes: [...previousData.nodes, ...newData.nodes],
                    },
                };
            },
        });
    };

    const onLoadLess = () => {
        fetchMore({
            variables: {
                limit: 3,
                offset: 0,
            },
            updateQuery: (_, { fetchMoreResult }) => {
                setIsLoadingMore(false);

                const newData = fetchMoreResult?.sessions ?? { nodes: [], totalCount: 0, totalDuration: 0 };
                return {
                    sessions: {
                        ...newData,
                    },
                };
            },
        });
    };

    return (
        <Container>
            <Typography variant="h4" color="primary" textAlign="center">
                {t("my_profile")}
            </Typography>
            {loading && <Loading />}
            {data && data.sessions.nodes.length === 0 && (
                <ImageContainer>
                    <StyledImage src={BusStop} />
                </ImageContainer>
            )}
            {data && data.sessions.nodes.length > 0 && (
                <>
                    <Typography textAlign="center" style={{ marginTop: 20 }}>
                        {t("overall_minutes1")}
                        <b>{secondsToTime(data.sessions.totalDuration)}</b>
                        {t("overall_minutes2")}
                    </Typography>
                    <Typography textAlign="center" style={{ marginTop: 20 }}>
                        {t("this_week")}
                        <b>{secondsToTime(data.sessions.totalDurationThisWeek)}</b> {t("minutes")}
                    </Typography>
                    <Chart data={data} />
                </>
            )}
            {data && (
                <>
                    <UserId />
                    <FavouritesList />
                    <ExcludedList />
                    <Settings />
                </>
            )}
            {data && data.sessions.nodes.length === 0 && (
                <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography color="primary" fontWeight="700" style={{ marginTop: 20, marginBottom: 10, fontSize: 20 }}>
                            {t("completed_sessions")}
                        </Typography>
                    </div>
                    <Typography textAlign="center">{t("no_sessions")}</Typography>
                </>
            )}
            {data && data.sessions.nodes.length > 0 && (
                <>
                    <SessionsList data={data} />
                    {data.sessions.nodes.length < data.sessions.totalCount && (
                        <LoadMoreButton isLoadingMore={isLoadingMore} onLoadMore={onLoadMore} />
                    )}

                    {data.sessions.nodes.length > 3 && data.sessions.nodes.length <= data.sessions.totalCount && (
                        <LoadLessButton onLoadLess={onLoadLess} />
                    )}
                </>
            )}

            {!loading && !data && <Error text={t("error")} />}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity={snackbar.variant} sx={{ width: "100%" }}>
                    {snackbar.text}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Profile;
